import { useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { apiGet, apiPost, apiPatch, apiDelete } from 'api';
import AuthContext from 'context/auth-context';

const getSections = async (token) => {
  const url = 'admin/sections';
  const data = await apiGet({ url, token });
  return data;
};

const getSection = async (token, id) => {
  const url = `admin/sections/${id}`;
  const data = await apiGet({ url, token });
  return data;
};

const createSection = async (token, params) => {
  const url = 'admin/sections';
  const data = await apiPost({ url, token, objectName: 'section', params });
  return data;
};

const updateSection = async (token, id, params) => {
  const url = `admin/sections/${id}`;
  const data = await apiPatch({ url, token, objectName: 'section', params });
  return data;
};

const deleteSection = async (token, id) => {
  const url = `admin/sections/${id}`;
  const data = await apiDelete({ url, token });
  return data;
};

export function useAdminSections({ options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getSections(token);
  const queryKey = 'admin-sections';
  return useQuery(queryKey, queryFn, { ...options });
}

export function useAdminSection({ id, options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getSection(token, id);
  const queryKey = ['admin-section', id];
  return useQuery(queryKey, queryFn, { enabled: !!id, ...options });
}

export function useCreateAdminSection({ options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => createSection(token, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-sections');
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useUpdateAdminSection({ id, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => updateSection(token, id, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-sections');
        queryClient.invalidateQueries(['admin-section', id]);
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useDeleteAdminSection({ options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (id) => deleteSection(token, id),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-sections');
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}
